import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { QtiResponse, QtiResponses } from '@/components/qti/types';
import { Button } from '@/components/ui/button';
import { AdminPageContent } from '@/features/admin/components/AdminPageContent';
import {
  AdminPageHeader,
  BreadcrumbItemProps,
} from '@/features/admin/components/AdminPageHeader';
import { useIsDebugMode } from '@/lib/utils';

import QtiRenderer from '../../components/qti/QtiRenderer';

export const Route = createFileRoute('/admin/qti-render-test')({
  component: RouteComponent,
  ssr: false,
});

// Define the type for each example
type QtiExample = {
  title: string;
  xml: string;
};

function RouteComponent() {
  const isDebugMode = useIsDebugMode();
  const [responses, setResponses] = useState<Record<number, QtiResponses>>({});
  const [submitted, setSubmitted] = useState<Record<number, boolean>>({});

  const handleResponseChange = (
    index: number,
    responseIdentifier: string,
    response: QtiResponse
  ) => {
    setResponses((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        [responseIdentifier]: response,
      },
    }));
  };

  const handleSubmittedChange = (index: number, submitted: boolean) => {
    setSubmitted((prev) => ({
      ...prev,
      [index]: submitted,
    }));
  };

  // Array of all QTI examples
  const examples: QtiExample[] = [
    {
      title: 'Choice Interaction (Single Choice)',
      xml: singleChoiceXml,
    },
    {
      title: 'Choice Interaction (Multiple Choice (1-3))',
      xml: multipleChoiceXml,
    },
    {
      title: 'Hottext Interaction (Single Selection)',
      xml: singleHottextXml,
    },
    {
      title: 'Hottext Interaction (Multiple Selection (1-3))',
      xml: multipleHottextXml,
    },
    {
      title: 'Text Entry Interaction',
      xml: textEntryXml,
    },
    {
      title: 'Numeric Text Entry Interaction',
      xml: numericEntryXml,
    },
    {
      title: 'Order Interaction',
      xml: orderInteractionXml,
    },
    {
      title: 'Inline Choice Interaction',
      xml: inlineChoiceInteractionXml,
    },
    {
      title: 'Gap Match Interaction',
      xml: gapMatchXml,
    },
    {
      title: 'Gap Match Interaction (Shuffled)',
      xml: gapMatchShuffledXml,
    },
    {
      title: 'Gap Match with empty response',
      xml: emptyResponseDeclarationXml,
    },
    {
      title: 'Match Interaction',
      xml: matchXml,
    },
    {
      title: 'Pre-filled Response',
      xml: preFilledResponseXml,
    },
  ];

  const breadcrumbItems: BreadcrumbItemProps[] = [
    {
      label: 'Admin',
      link: {
        to: '/admin',
      },
    },
    {
      label: 'QTI Renderer Test',
      link: {
        to: '/admin/qti-render-test',
      },
    },
  ];

  return (
    <AdminPageContent>
      <AdminPageHeader breadcrumbItems={breadcrumbItems} />
      <div className="container mx-auto p-4">
        <h1 className="text-3xl font-bold">QTI Renderer Test</h1>

        <div className="grid gap-4 mt-4">
          {examples.map((example, index) => (
            <div
              key={index}
              className="border rounded-lg p-4 shadow-sm bg-white overflow-x-auto"
            >
              <h2 className="text-xl font-semibold mb-4">{example.title}</h2>
              <QtiRenderer
                xml={example.xml}
                responses={responses[index] ?? {}}
                onResponseChange={(responseIdentifier, response) =>
                  handleResponseChange(index, responseIdentifier, response)
                }
                displayResponseFeedback={submitted[index]}
                isDebugMode={isDebugMode}
              />
              <div className="mt-2">
                {submitted[index] ? (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => handleSubmittedChange(index, false)}
                  >
                    Clear Response
                  </Button>
                ) : (
                  <Button
                    variant="success"
                    className="w-full"
                    onClick={() => handleSubmittedChange(index, true)}
                  >
                    Submit
                  </Button>
                )}
              </div>
              <div className="mt-4 p-3 bg-gray-50 rounded border">
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Response:
                </h3>
                <pre className="text-sm overflow-x-auto">
                  {JSON.stringify(
                    Array.isArray(responses[index])
                      ? responses[index] || []
                      : responses[index] || '',
                    null,
                    2
                  )}
                </pre>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminPageContent>
  );
}

const matchXml = `
<qti-assessment-item xmlns="http://www.imsglobal.org/xsd/imsqtiasi_v3p0" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.imsglobal.org/xsd/imsqtiasi_v3p0 https://purl.imsglobal.org/spec/qti/v3p0/schema/xsd/imsqti_asiv3p0p1_v1p0.xsd" identifier="incept-0b0db225-6d89-4e9e-8c8e-18280b0198a5" title="L.3.4.b - Group words by shared affix meaning (prefixes and suffixes)" time-dependent="false" xml:lang="en-US">
  <qti-response-declaration identifier="RESPONSE" cardinality="multiple" base-type="directedPair">
    <qti-correct-response>
      <qti-value>A P</qti-value>
      <qti-value>B P</qti-value>
      <qti-value>C P</qti-value>
      <qti-value>D S</qti-value>
      <qti-value>E S</qti-value>
      <qti-value>F S</qti-value>
    </qti-correct-response>
  </qti-response-declaration>
  <qti-outcome-declaration identifier="FEEDBACK" cardinality="single" base-type="identifier">
  </qti-outcome-declaration>
  <qti-outcome-declaration identifier="FEEDBACK-INLINE" cardinality="multiple" base-type="identifier">
  </qti-outcome-declaration>
  <qti-item-body>
    <p>Sort the words based on the meaning of their prefix or suffix.</p>
    <qti-match-interaction response-identifier="RESPONSE" shuffle="false">
      <qti-simple-match-set>
        <qti-simple-associable-choice identifier="A" match-max="1">uncover</qti-simple-associable-choice>
        <qti-simple-associable-choice identifier="B" match-max="1">unsafe</qti-simple-associable-choice>
        <qti-simple-associable-choice identifier="C" match-max="1">unlucky</qti-simple-associable-choice>
        <qti-simple-associable-choice identifier="D" match-max="1">fearless</qti-simple-associable-choice>
        <qti-simple-associable-choice identifier="E" match-max="1">hopeless</qti-simple-associable-choice>
        <qti-simple-associable-choice identifier="F" match-max="1">dustless</qti-simple-associable-choice>
      </qti-simple-match-set>
      <qti-simple-match-set>
        <qti-simple-associable-choice identifier="P" match-max="6">prefix means not (un-)</qti-simple-associable-choice>
        <qti-simple-associable-choice identifier="S" match-max="6">suffix means without (-less)</qti-simple-associable-choice>
      </qti-simple-match-set>
    </qti-match-interaction>
<qti-feedback-block outcome-identifier="FEEDBACK" identifier="CORRECT" show-hide="show">
  <qti-content-body>
    <p>    Great job! “Uncover,” “unsafe,” and “unlucky” all start with <em>un-</em>, so they mean “not covered,” “not safe,” and “not lucky.” “Fearless,” “hopeless,” and “dustless” all end with <em>-less</em>, so they mean “without fear,” “without hope,” and “without dust.”
    </p>
  </qti-content-body>
</qti-feedback-block>
<qti-feedback-block outcome-identifier="FEEDBACK" identifier="INCORRECT" show-hide="show">
  <qti-content-body>
    <p>    Check where the added letters appear. The prefix <em>un-</em> at the beginning of a word means “not,” as in “uncover” (not covered). The suffix <em>-less</em> at the end means “without,” as in “fearless” (without fear). Look for these clues and try matching the words again.
    </p>
  </qti-content-body>
</qti-feedback-block>
  </qti-item-body>
<qti-response-processing template="https://purl.imsglobal.org/spec/qti/v3p0/rptemplates/match_correct.xml"/></qti-assessment-item>`;

const gapMatchXml = `
<qti-assessment-item xmlns="http://www.imsglobal.org/xsd/imsqtiasi_v3p0" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.imsglobal.org/xsd/imsqtiasi_v3p0 https://purl.imsglobal.org/spec/qti/v3p0/schema/xsd/imsqti_asiv3p0p1_v1p0.xsd" identifier="incept-60bfb7cc-5b78-4b1d-9522-0e3dc2e2181c" title="L.5 - Match an informational detail to the most precise heading" time-dependent="false" xml:lang="en-US">
  <qti-response-declaration identifier="RESPONSE" cardinality="multiple" base-type="directedPair">
    <qti-correct-response>
      <qti-value>pos1 protection</qti-value>
      <qti-value>pos2 dangers</qti-value>
      <qti-value>pos3 behavior</qti-value>
    </qti-correct-response>
  </qti-response-declaration>
  <qti-outcome-declaration identifier="FEEDBACK" cardinality="single" base-type="identifier">
  </qti-outcome-declaration>
  <qti-outcome-declaration identifier="FEEDBACK-INLINE" cardinality="multiple" base-type="identifier">
  </qti-outcome-declaration>
  <qti-item-body>
<div><h4>Asha is writing an article about desert horned lizards. Read each detail and associate each detail with a topic that best fits.</h4></div>
    <qti-gap-match-interaction response-identifier="RESPONSE" shuffle="false">
      <qti-gap-text identifier="behavior" match-max="1">Behavior</qti-gap-text>
      <qti-gap-text identifier="dangers" match-max="1">Dangers</qti-gap-text>
      <qti-gap-text identifier="protection" match-max="1">Protection</qti-gap-text>
<table><thead><tr><th style="width: 80%;">Sentence</th><th style="width: 20%;">Topic</th></tr></thead><tbody><tr><td>To startle coyotes, the desert horned lizard can squirt blood from its eyes.</td><td><qti-gap identifier="pos1"/> </td></tr><tr><td>Rattlesnakes and domestic cats often prey on young horned lizards.</td><td><qti-gap identifier="pos2"/> </td></tr><tr><td>Each morning, the lizard lies flat on the sand to soak up heat.</td><td><qti-gap identifier="pos3"/> </td></tr></tbody></table>    </qti-gap-match-interaction>
<qti-feedback-block outcome-identifier="FEEDBACK" identifier="CORRECT" show-hide="show">
  <qti-content-body>
    <p>    <strong>Nice job!</strong><br/><br/>When you sort facts into headings, first ask, “What kind of information is this sentence giving me?”<br/><br/><br/><br/><strong>Step 1 – Remember the three headings</strong><br/><br/>• <em>Behavior</em>: what the lizard normally does<br/><br/>• <em>Dangers</em>: things that can harm the lizard<br/><br/>• <em>Protection</em>: tricks the lizard uses to stay safe<br/><br/><br/><br/><strong>Step 2 – Check the key words in each sentence</strong><br/><br/>1. “Squirt blood from its eyes” is a special trick the lizard uses to scare away a coyote. That is a way to stay safe, so it belongs under <em>Protection</em>.<br/><br/>2. “Rattlesnakes and domestic cats prey on young horned lizards.” These animals are threats, so this detail fits the <em>Dangers</em> heading.<br/><br/>3. “Each morning, the lizard lies flat on the sand to soak up heat.” Lying on the sand every morning is part of the lizard’s daily routine. That describes what it does, so it goes under <em>Behavior</em>.<br/><br/><br/><br/>By matching the meaning of each sentence to the right heading, you placed every fact where it belongs. Great sorting!
    </p>
  </qti-content-body>
</qti-feedback-block>
<qti-feedback-block outcome-identifier="FEEDBACK" identifier="INCORRECT" show-hide="show">
  <qti-content-body>
    <p>    <strong>Let’s look at how to match each fact to the best heading.</strong><br/><br/><br/><br/>1. Read one sentence at a time and find the main idea.<br/><br/>2. Decide which question the sentence answers:<br/><br/>  • “What does the lizard normally do?” → That fits <em>Behavior</em>.<br/><br/>  • “What can hurt the lizard?” → That belongs to <em>Dangers</em>.<br/><br/>  • “How does the lizard keep itself safe?” → That is <em>Protection</em>.<br/><br/><br/><br/>Look for action words like “prey on,” which signal danger, or words that show the lizard is doing something every day, which signal a behavior. Tricks that scare predators point to protection.<br/><br/><br/><br/>Use these clues to place each sentence under the heading that best matches its meaning.
    </p>
  </qti-content-body>
</qti-feedback-block>
  </qti-item-body>
<qti-response-processing template="https://purl.imsglobal.org/spec/qti/v3p0/rptemplates/match_correct.xml"/></qti-assessment-item>`;

// Example XML for single choice interaction
const singleChoiceXml = `
<qti-assessment-item>
  <qti-response-declaration base-type="identifier" cardinality="single" identifier="RESPONSE">
    <qti-correct-response>
      <qti-value>A</qti-value>
    </qti-correct-response>
  </qti-response-declaration>
  <qti-item-body>
    <qti-choice-interaction response-identifier="RESPONSE" max-choices="1" shuffle="true">
      <qti-prompt>What is the capital of France?</qti-prompt>
      <qti-simple-choice identifier="A">
        Paris
        <qti-feedback-inline outcome-identifier="FEEDBACK-INLINE" identifier="A" show-hide="show">
          <span style="color: #2E8B57;">Correct! Paris is the capital of France</span>
        </qti-feedback-inline>
      </qti-simple-choice>
      <qti-simple-choice identifier="B">
        London
        <qti-feedback-inline outcome-identifier="FEEDBACK-INLINE" identifier="B" show-hide="show">
          <span style="color: #D9534F;">Incorrect. London is the capital of England</span>
        </qti-feedback-inline>
      </qti-simple-choice>
      <qti-simple-choice identifier="C">
        Berlin
        <qti-feedback-inline outcome-identifier="FEEDBACK-INLINE" identifier="C" show-hide="show">
          <span style="color: #D9534F;">Incorrect. Berlin is the capital of Germany</span>
        </qti-feedback-inline>
      </qti-simple-choice>
      <qti-simple-choice identifier="D">
        Madrid
        <qti-feedback-inline outcome-identifier="FEEDBACK-INLINE" identifier="D" show-hide="show">
          <span style="color: #D9534F;">Incorrect. Madrid is the capital of Spain</span>
        </qti-feedback-inline>
      </qti-simple-choice>
    </qti-choice-interaction>
    <qti-feedback-block identifier="CORRECT" show-hide="show" outcome-identifier="CORRECT">
      <qti-content-body>
        <div>
          <p>
            <span style="color: #2E8B57;">Correct.</span>
          </p>
        </div>
      </qti-content-body>
    </qti-feedback-block>
    <qti-feedback-block identifier="INCORRECT" show-hide="show" outcome-identifier="INCORRECT">
      <qti-content-body>
        <div>
          <p>
            <span style="color: #D9534F;">Incorrect. "Paris" is the capital of France</span>
          </p>
        </div>
      </qti-content-body>
    </qti-feedback-block>
  </qti-item-body>
</qti-assessment-item>
`;

// Example XML for multiple choice interaction
const multipleChoiceXml = `
<qti-assessment-item>
  <qti-response-declaration base-type="identifier" cardinality="multiple" identifier="RESPONSE">
    <qti-correct-response>
      <qti-value>B</qti-value>
      <qti-value>D</qti-value>
      <qti-value>E</qti-value>
    </qti-correct-response>
  </qti-response-declaration>
  <qti-item-body>
    <qti-choice-interaction response-identifier="RESPONSE" max-choices="3" min-choices="1" shuffle="true">
      <qti-prompt>Select all programming languages that are statically typed:</qti-prompt>
      <qti-simple-choice identifier="A">
        JavaScript
        <qti-feedback-inline outcome-identifier="FEEDBACK-INLINE" identifier="A" show-hide="show">
          <span style="color: #D9534F;">Incorrect. JavaScript is a dynamically typed language</span>
        </qti-feedback-inline>
      </qti-simple-choice>
      <qti-simple-choice identifier="B">
        TypeScript
        <qti-feedback-inline outcome-identifier="FEEDBACK-INLINE" identifier="B" show-hide="show">
          <span style="color: #2E8B57;">Correct! TypeScript is a statically typed language</span>
        </qti-feedback-inline>
      </qti-simple-choice>
      <qti-simple-choice identifier="C">
        Python
        <qti-feedback-inline outcome-identifier="FEEDBACK-INLINE" identifier="C" show-hide="show">
          <span style="color: #D9534F;">Incorrect. Python is a dynamically typed language</span>
        </qti-feedback-inline>
      </qti-simple-choice>
      <qti-simple-choice identifier="D">
        Java
        <qti-feedback-inline outcome-identifier="FEEDBACK-INLINE" identifier="D" show-hide="show">
          <span style="color: #2E8B57;">Correct! Java is a statically typed language</span>
        </qti-feedback-inline>
      </qti-simple-choice>
      <qti-simple-choice identifier="E">
        C++
        <qti-feedback-inline outcome-identifier="FEEDBACK-INLINE" identifier="E" show-hide="show">
          <span style="color: #2E8B57;">Correct! C++ is a statically typed language</span>
        </qti-feedback-inline>
      </qti-simple-choice>
    </qti-choice-interaction>
    <qti-feedback-block identifier="CORRECT" show-hide="show" outcome-identifier="CORRECT">
      <qti-content-body>
        <div>
          <p>
            <span style="color: #2E8B57;">Correct.</span>
          </p>
        </div>
      </qti-content-body>
    </qti-feedback-block>
    <qti-feedback-block identifier="INCORRECT" show-hide="show" outcome-identifier="INCORRECT">
      <qti-content-body>
        <div>
          <p>
            <span style="color: #D9534F;">Incorrect. Only "TypeScript", "Java", and "C++" are statically typed languages</span>
          </p>
        </div>
      </qti-content-body>
    </qti-feedback-block>
  </qti-item-body>
</qti-assessment-item>
`;
// Example XML for single hottext interaction
const singleHottextXml = `
<qti-assessment-item>
  <qti-response-declaration base-type="identifier" cardinality="single" identifier="RESPONSE">
    <qti-correct-response>
      <qti-value>C</qti-value>
    </qti-correct-response>
  </qti-response-declaration>
  <qti-item-body>
    <qti-hottext-interaction response-identifier="RESPONSE" max-choices="1">
      <qti-prompt>Select the noun in the following sentence:</qti-prompt>
      <p>The <qti-hottext identifier="A">quick</qti-hottext> <qti-hottext identifier="B">brown</qti-hottext> <qti-hottext identifier="C">fox</qti-hottext> <qti-hottext identifier="D">jumps</qti-hottext> over the lazy dog.</p>
    </qti-hottext-interaction>
    <qti-feedback-block identifier="INCORRECT" show-hide="show" outcome-identifier="INCORRECT">
      <qti-content-body>
        <div>
          <p>
            <span style="color: #D9534F;">Incorrect. Only "fox" is the noun</span>
          </p>
        </div>
      </qti-content-body>
    </qti-feedback-block>
    <qti-feedback-block identifier="CORRECT" show-hide="show" outcome-identifier="CORRECT">
      <qti-content-body>
        <div>
          <p>
            <span style="color: #2E8B57;">Correct.</span>
          </p>
        </div>
      </qti-content-body>
    </qti-feedback-block>
  </qti-item-body>
</qti-assessment-item>
`;

// Example XML for multiple hottext interaction
const multipleHottextXml = `
<qti-assessment-item>
  <qti-response-declaration base-type="identifier" cardinality="multiple" identifier="RESPONSE">
    <qti-correct-response>
      <qti-value>A</qti-value>
      <qti-value>B</qti-value>
    </qti-correct-response>
  </qti-response-declaration>
  <qti-item-body>
    <qti-hottext-interaction response-identifier="RESPONSE" max-choices="3" min-choices="1">
      <qti-prompt>Select all the adjectives in the following sentence:</qti-prompt>
      <p>The <qti-hottext identifier="A">quick</qti-hottext> <qti-hottext identifier="B">brown</qti-hottext> <qti-hottext identifier="C">fox</qti-hottext> <qti-hottext identifier="D">jumps</qti-hottext> over the lazy dog.</p>
    </qti-hottext-interaction>
    <qti-feedback-block identifier="INCORRECT" show-hide="show" outcome-identifier="INCORRECT">
      <qti-content-body>
        <div>
          <p>
            <span style="color: #D9534F;">Incorrect. Only "quick" and "brown" are adjectives</span>
          </p>
        </div>
      </qti-content-body>
    </qti-feedback-block>
    <qti-feedback-block identifier="CORRECT" show-hide="show" outcome-identifier="CORRECT">
      <qti-content-body>
        <div>
          <p>
            <span style="color: #2E8B57;">Correct.</span>
          </p>
        </div>
      </qti-content-body>
    </qti-feedback-block>
  </qti-item-body>
</qti-assessment-item>
`;

// Example XML for text entry interaction
const textEntryXml = `
<qti-assessment-item
  xmlns="http://www.imsglobal.org/xsd/imsqtiasi_v3p0" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="http://www.imsglobal.org/xsd/imsqtiasi_v3p0 https://purl.imsglobal.org/spec/qti/v3p0/schema/xsd/imsqti_asiv3p0_v1p0.xsd"
  adaptive="false" time-dependent="false"
  identifier="text-entry-1" title="Text Entry Example">
  <qti-response-declaration base-type="identifier" cardinality="single" identifier="RESPONSE">
    <qti-correct-response>
      <qti-value>sun</qti-value>
    </qti-correct-response>
  </qti-response-declaration>
  <qti-outcome-declaration base-type="float" cardinality="single" identifier="SCORE"/>
  <qti-item-body>
    <p>Complete the sentence with the appropriate word:</p>
    <p>
      The Earth orbits around the
      <qti-text-entry-interaction response-identifier="RESPONSE" placeholder="Type your answer here"/>.
    </p>
    <qti-feedback-block identifier="INCORRECT" show-hide="show" outcome-identifier="INCORRECT">
      <qti-content-body>
        <div>
          <p>
            <span style="color: #D9534F;">Incorrect. The Earth orbits around the "sun"</span>
          </p>
        </div>
      </qti-content-body>
    </qti-feedback-block>
    <qti-feedback-block identifier="CORRECT" show-hide="show" outcome-identifier="CORRECT">
      <qti-content-body>
        <div>
          <p>
            <span style="color: #2E8B57;">Correct.</span>
          </p>
        </div>
      </qti-content-body>
    </qti-feedback-block>
  </qti-item-body>
</qti-assessment-item>
`;

// Example XML for numeric text entry interaction
const numericEntryXml = `
<qti-assessment-item
  xmlns="http://www.imsglobal.org/xsd/imsqtiasi_v3p0" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="http://www.imsglobal.org/xsd/imsqtiasi_v3p0 https://purl.imsglobal.org/spec/qti/v3p0/schema/xsd/imsqti_asiv3p0_v1p0.xsd"
  adaptive="false" time-dependent="false"
  identifier="numeric-entry-1" title="Numeric Text Entry Example">
  <qti-response-declaration base-type="float" cardinality="single" identifier="RESPONSE">
    <qti-correct-response>
      <qti-value>3.14</qti-value>
    </qti-correct-response>
  </qti-response-declaration>
  <qti-outcome-declaration base-type="float" cardinality="single" identifier="SCORE"/>
  <qti-item-body>
    <p>What is the value of π rounded to 2 decimal places?</p>
    <p>
      π ≈ <qti-text-entry-interaction response-identifier="RESPONSE" pattern-mask="[0-9]+.[0-9]{2}" placeholder="e.g., 3.14"/>
    </p>
    <qti-modal-feedback identifier="INCORRECT" show-hide="show" outcome-identifier="INCORRECT">
      <qti-content-body>
        <div>
          <p>
            <span style="color: #D9534F;">Incorrect. Should be "3.14"</span>
          </p>
        </div>
      </qti-content-body>
    </qti-modal-feedback>
    <qti-modal-feedback identifier="CORRECT" show-hide="show" outcome-identifier="CORRECT">
      <qti-content-body>
        <div>
          <p>
            <span style="color: #2E8B57;">Correct.</span>
          </p>
        </div>
      </qti-content-body>
    </qti-modal-feedback>
  </qti-item-body>
</qti-assessment-item>
`;

// Example XML for order interaction
const orderInteractionXml = `
<qti-assessment-item xmlns="http://www.imsglobal.org/xsd/imsqtiasi_v3p0" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.imsglobal.org/xsd/imsqtiasi_v3p0 https://purl.imsglobal.org/spec/qti/v3p0/schema/xsd/imsqti_asiv3p0_v1p0.xsd" identifier="q15-order-example-1" title="Grand Prix of Bahrain" adaptive="false" time-dependent="false">
    <qti-response-declaration identifier="RESPONSE" cardinality="ordered" base-type="identifier">
      <qti-correct-response>
        <qti-value>DriverA</qti-value>
        <qti-value>DriverB</qti-value>
        <qti-value>DriverC</qti-value>
      </qti-correct-response>
    </qti-response-declaration>
    <qti-outcome-declaration identifier="SCORE" cardinality="single" base-type="float"/>
    <qti-item-body>
      <qti-order-interaction response-identifier="RESPONSE" shuffle="true">
        <qti-prompt>The following F1 drivers finished on the podium in the first ever Grand Prix of Bahrain. Can you rearrange them into the correct finishing order from left to right, 1st, 2nd, and 3rd?</qti-prompt>
        <qti-simple-choice identifier="DriverA">Rubens Barrichello</qti-simple-choice>
        <qti-simple-choice identifier="DriverB">Jenson Button</qti-simple-choice>
        <qti-simple-choice identifier="DriverC">Michael Schumacher</qti-simple-choice>
      </qti-order-interaction>
      <p>
        Note: This conformance example has no QTI 3 shared presentation vocabulary.  Consequently, QTI 3 Player renders the <em>Order Interaction</em> above with QTI 3 Player defaults; i.e., as a series of choices with horizontal orientation.
      </p>
        <qti-modal-feedback identifier="INCORRECT" show-hide="show" outcome-identifier="INCORRECT">
        <qti-content-body>
          <div>
            <p>
              <span style="color: #D9534F;">Incorrect.</span>
            </p>
          </div>
        </qti-content-body>
      </qti-modal-feedback>
      <qti-modal-feedback identifier="CORRECT" show-hide="show" outcome-identifier="CORRECT">
        <qti-content-body>
          <div>
            <p>
              <span style="color: #2E8B57;">Correct.</span>
            </p>
          </div>
        </qti-content-body>
      </qti-modal-feedback>
    </qti-item-body>
    <qti-response-processing template="https://purl.imsglobal.org/spec/qti/v3p0/rptemplates/match_correct.xml"/>
  </qti-assessment-item>
`;

const inlineChoiceInteractionXml = `
<qti-assessment-item xmlns="http://www.imsglobal.org/xsd/imsqtiasi_v3p0" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.imsglobal.org/xsd/imsqtiasi_v3p0 https://purl.imsglobal.org/spec/qti/v3p0/schema/xsd/imsqti_asiv3p0_v1p0.xsd" identifier="inline-choice-physics" title="Physics Inline Choice" adaptive="false" time-dependent="false">
    <qti-response-declaration identifier="RESPONSE1" cardinality="single" base-type="identifier">
        <qti-correct-response>
            <qti-value>larger</qti-value>
        </qti-correct-response>
    </qti-response-declaration>
    <qti-response-declaration identifier="RESPONSE2" cardinality="single" base-type="identifier">
        <qti-correct-response>
            <qti-value>weaker</qti-value>
        </qti-correct-response>
    </qti-response-declaration>
    <qti-outcome-declaration identifier="SCORE" cardinality="single" base-type="float">
        <qti-default-value>
            <qti-value>0</qti-value>
        </qti-default-value>
    </qti-outcome-declaration>
    <qti-outcome-declaration identifier="FEEDBACK" cardinality="single" base-type="identifier"/>
    <qti-outcome-declaration identifier="FEEDBACK-INLINE" cardinality="single" base-type="identifier"/>
    <qti-item-body>
        <div>
            <p><strong>Complete the statements.</strong></p>
            <ul>
                <li>
                    <p>Two objects are acted on by the same strength net force. The object that experiences the smaller acceleration must have a
                        <qti-inline-choice-interaction response-identifier="RESPONSE1">
                            <qti-inline-choice identifier="larger">larger</qti-inline-choice>
                            <qti-inline-choice identifier="smaller">smaller</qti-inline-choice>
                            <qti-inline-choice identifier="equal">equal</qti-inline-choice>
                        </qti-inline-choice-interaction> mass.
                    </p>
                </li>
                <li>
                    <p>Two objects with the same mass are accelerating. The object that experiences the smaller acceleration must have a
                        <qti-inline-choice-interaction response-identifier="RESPONSE2">
                            <qti-inline-choice identifier="stronger">stronger</qti-inline-choice>
                            <qti-inline-choice identifier="weaker">weaker</qti-inline-choice>
                            <qti-inline-choice identifier="equal">equal</qti-inline-choice>
                        </qti-inline-choice-interaction> net force acting on it.
                    </p>
                </li>
            </ul>
        </div>
        <qti-feedback-block outcome-identifier="FEEDBACK" identifier="CORRECT" show-hide="show">
            <qti-content-body>
                <p>Correct! You've successfully applied Newton's Second Law of Motion.</p>
            </qti-content-body>
        </qti-feedback-block>
        <qti-feedback-block outcome-identifier="FEEDBACK" identifier="INCORRECT" show-hide="show">
            <qti-content-body>
                <p>Incorrect. Remember that according to Newton's Second Law, F = ma. Consider how mass and force affect acceleration.</p>
            </qti-content-body>
        </qti-feedback-block>
    </qti-item-body>
    <qti-response-processing>
        <qti-response-condition>
            <qti-response-if>
                <qti-and>
                    <qti-match>
                        <qti-variable identifier="RESPONSE1"/>
                        <qti-correct identifier="RESPONSE1"/>
                    </qti-match>
                    <qti-match>
                        <qti-variable identifier="RESPONSE2"/>
                        <qti-correct identifier="RESPONSE2"/>
                    </qti-match>
                </qti-and>
                <qti-set-outcome-value identifier="SCORE">
                    <qti-base-value base-type="float">1</qti-base-value>
                </qti-set-outcome-value>
                <qti-set-outcome-value identifier="FEEDBACK">
                    <qti-base-value base-type="identifier">CORRECT</qti-base-value>
                </qti-set-outcome-value>
            </qti-response-if>
            <qti-response-else>
                <qti-set-outcome-value identifier="SCORE">
                    <qti-base-value base-type="float">0</qti-base-value>
                </qti-set-outcome-value>
                <qti-set-outcome-value identifier="FEEDBACK">
                    <qti-base-value base-type="identifier">INCORRECT</qti-base-value>
                </qti-set-outcome-value>
            </qti-response-else>
        </qti-response-condition>
    </qti-response-processing>
</qti-assessment-item>
`;

const emptyResponseDeclarationXml = `
<qti-assessment-item xmlns="http://www.imsglobal.org/xsd/imsqtiasi_v3p0"
    xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
    xsi:schemaLocation="http://www.imsglobal.org/xsd/imsqtiasi_v3p0 https://purl.imsglobal.org/spec/qti/v3p0/schema/xsd/imsqti_asiv3p0p1_v1p0.xsd"
    identifier="incept-cd525420-e490-49cc-b947-e218a6fd1a7d"
    title="L.3 - Insert Missing Commas in Sentences"
    time-dependent="false" xml:lang="en-US">
    <qti-response-declaration identifier="RESPONSE" cardinality="multiple" base-type="directedPair">
        <qti-correct-response> </qti-correct-response>
    </qti-response-declaration>
    <qti-outcome-declaration identifier="FEEDBACK" cardinality="single" base-type="identifier"> </qti-outcome-declaration>
    <qti-outcome-declaration identifier="FEEDBACK-INLINE" cardinality="multiple"
        base-type="identifier"> </qti-outcome-declaration>
    <qti-item-body>
        <div></div>
        <div>
            <h4>Correct the sentence by adding any missing commas. If the sentence is already
            correct, don't add any commas.</h4>
        </div>
        <qti-gap-match-interaction response-identifier="RESPONSE" shuffle="false">
            <qti-gap-text identifier="comma" match-max="1">,</qti-gap-text>
            <p>Many <qti-gap identifier="pos1" /> travellers <qti-gap identifier="pos2" /> shop <qti-gap
                    identifier="pos3" /> at <qti-gap identifier="pos4" /> markets <qti-gap
                    identifier="pos5" /> in <qti-gap identifier="pos6" /> Quito <qti-gap
                    identifier="pos7" /> in <qti-gap
                    identifier="pos8" /> Ecuador <qti-gap identifier="pos9" /> for <qti-gap
                    identifier="pos10" /> colorful <qti-gap identifier="pos11" /> woven <qti-gap
                    identifier="pos12" /> baskets.</p>
        </qti-gap-match-interaction>
        <qti-feedback-block outcome-identifier="FEEDBACK" identifier="CORRECT" show-hide="show">
            <qti-content-body>
                <p>
                    <strong>Nice work!</strong><br />Here’s the rule: When a city and its state or
                country sit <em>right next to each other</em>, you put a comma between them—like
                this: “Paris, France.”<br /><br />Let’s check the sentence step by step:<br />1.
                Find the place names. We see <em>Quito</em> and <em>Ecuador</em>.<br />2. Ask, “Are
                they side by side?” No. The word <em>in</em> sits between them: “Quito <em> in</em>
                Ecuador.”<br />3. Because a word separates the two place names, the city-country
                comma rule does <em>not</em> apply.<br />4. No other lists or extra information need
                commas.<br /><br />So the sentence is already correct without any commas. </p>
            </qti-content-body>
        </qti-feedback-block>
        <qti-feedback-block outcome-identifier="FEEDBACK" identifier="INCORRECT" show-hide="show">
            <qti-content-body>
                <p>
                    <strong>Let’s look at the comma rule together.</strong><br />We only use a comma
                when a city and its state or country come <em>right next to each other</em>: “Miami,
                Florida.”<br /><br />Step by step:<br />1. Spot the place names: <em> Quito</em> and <em>
                Ecuador</em>.<br />2. Notice the word <em>in</em> between them: “Quito <em>in</em>
                Ecuador.”<br />3. Because the two place names are not side by side, the city-country
                comma rule does not apply here.<br />4. There are no lists or extra information that
                need commas elsewhere in the sentence.<br /><br />That’s why the sentence should
                have <em>no commas</em>. Removing any commas you added makes it correct. </p>
            </qti-content-body>
        </qti-feedback-block>
    </qti-item-body>
    <qti-response-processing
        template="https://purl.imsglobal.org/spec/qti/v3p0/rptemplates/match_correct.xml" />
</qti-assessment-item>
`;

const preFilledResponseXml = `
<qti-assessment-item
  xmlns="http://www.imsglobal.org/xsd/imsqtiasi_v3p0"
  identifier="demo-default"
  title="Default‑value test"
  time-dependent="false"
>
  <qti-response-declaration
    identifier="RESPONSE"
    cardinality="single"
    base-type="string"
  >
    <qti-default-value>
      <qti-value>foo</qti-value>
    </qti-default-value>
    <qti-correct-response>
      <qti-value>foobar</qti-value>
    </qti-correct-response>
  </qti-response-declaration>
  <qti-response-declaration
    identifier="RESPONSE2"
    cardinality="single"
    base-type="string"
  >
    <qti-default-value>
      <qti-value>bar</qti-value>
    </qti-default-value>
    <qti-correct-response>
      <qti-value>foobar</qti-value>
    </qti-correct-response>
  </qti-response-declaration>

  <qti-item-body>
    Type here:
    <qti-text-entry-interaction response-identifier="RESPONSE" />
    Another input:
    <qti-text-entry-interaction response-identifier="RESPONSE2" />
    
    <qti-feedback-block
      outcome-identifier="FEEDBACK"
      identifier="CORRECT"
      show-hide="show"
    >
      <qti-content-body>
        <p>
          <strong>Nice work!</strong>
        </p>
      </qti-content-body>
    </qti-feedback-block>
    <qti-feedback-block
      outcome-identifier="FEEDBACK"
      identifier="INCORRECT"
      show-hide="show"
    >
      <qti-content-body>
        <p>
          <strong>Wrong answer!</strong>
        </p>
      </qti-content-body>
    </qti-feedback-block>
  </qti-item-body>

  <qti-response-processing
    template="https://purl.imsglobal.org/spec/qti/v3p0/rptemplates/match_correct.xml"
  />
</qti-assessment-item>
`;

const gapMatchShuffledXml = `
<qti-assessment-item
  xmlns="http://www.imsglobal.org/xsd/imsqtiasi_v3p0"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="http://www.imsglobal.org/xsd/imsqtiasi_v3p0 https://purl.imsglobal.org/spec/qti/v3p0/schema/xsd/imsqti_asiv3p0p1_v1p0.xsd"
  identifier="incept-f919ff3c-6e73-49d2-af4d-035d19255a8b"
  title="L.5 - Match an informational detail to the most precise heading"
  time-dependent="false"
  xml:lang="en-US"
>
  <qti-response-declaration
    identifier="RESPONSE"
    cardinality="multiple"
    base-type="directedPair"
  >
    <qti-correct-response>
      <qti-value>pos1 size</qti-value>
      <qti-value>pos2 weight</qti-value>
      <qti-value>pos3 material</qti-value>
      <qti-value>pos4 purpose</qti-value>
    </qti-correct-response>
  </qti-response-declaration>
  <qti-outcome-declaration
    identifier="FEEDBACK"
    cardinality="single"
    base-type="identifier"
  >
  </qti-outcome-declaration>
  <qti-outcome-declaration
    identifier="FEEDBACK-INLINE"
    cardinality="multiple"
    base-type="identifier"
  >
  </qti-outcome-declaration>
  <qti-item-body>
    <div>
      <h4>
        A civil engineer is writing a report about a new highway bridge. Read
        each detail and associate each detail with a topic that best fits.
      </h4>
    </div>
    <qti-gap-match-interaction
      response-identifier="RESPONSE"
      shuffle="true"
      max-associations="4"
    >
      <qti-gap-text identifier="size" match-max="0">Size</qti-gap-text>
      <qti-gap-text identifier="weight" match-max="0">Weight</qti-gap-text>
      <qti-gap-text identifier="material" match-max="0">Material</qti-gap-text>
      <qti-gap-text identifier="purpose" match-max="0">Purpose</qti-gap-text>
      <table>
        <thead>
          <tr>
            <th style="width: 80%">Sentence</th>
            <th style="width: 20%">Topic</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              The main support beam measures three feet wide, six feet deep, and
              140 feet long.
            </td>
            <td><qti-gap identifier="pos1" /></td>
          </tr>
          <tr>
            <td>When completed, each beam weighs just over fifty-four tons.</td>
            <td><qti-gap identifier="pos2" /></td>
          </tr>
          <tr>
            <td>
              Engineers chose steel that forms a thin, protective rust coat.
            </td>
            <td><qti-gap identifier="pos3" /></td>
          </tr>
          <tr>
            <td>
              The beam supports the outer traffic lane and carries loads to the
              piers.
            </td>
            <td><qti-gap identifier="pos4" /></td>
          </tr>
        </tbody>
      </table>
    </qti-gap-match-interaction>
    <qti-feedback-block
      outcome-identifier="FEEDBACK"
      identifier="CORRECT"
      show-hide="show"
    >
      <qti-content-body>
        <p>
          <strong
            >Fantastic work! You put every bridge fact under the perfect
            heading.</strong
          ><br /><br /><strong>Quick reminder of the rule:</strong><br />•
          <em>Size</em> tells how big something is—length, width, height,
          depth.<br />• <em>Weight</em> tells how heavy something is.<br />•
          <em>Material</em> tells what it is made of.<br />•
          <em>Purpose</em> tells the job it does.<br /><br /><strong
            >How the facts match the rule:</strong
          ><br />• "The main support beam measures three feet wide, six feet
          deep, and 140 feet long." → <em>Size</em> because the sentence lists
          the beam's exact measurements.<br />• "When completed, each beam
          weighs just over fifty-four tons." → <em>Weight</em> because the word
          "weighs" and the number of tons tell how heavy it is.<br />•
          "Engineers chose steel that forms a thin, protective rust coat." →
          <em>Material</em> because it tells the beam is made of steel.<br />•
          "The beam supports the outer traffic lane and carries loads to the
          piers." → <em>Purpose</em> because it tells what the beam does—support
          and carry loads.<br /><br />You spotted the key clue
          words—measurements, tons, steel, and action verbs—and used them to
          choose the right headings. Nicely done!
        </p>
      </qti-content-body>
    </qti-feedback-block>
    <qti-feedback-block
      outcome-identifier="FEEDBACK"
      identifier="INCORRECT"
      show-hide="show"
    >
      <qti-content-body>
        <p>
          <strong
            >Let's place each bridge detail under the heading that fits
            best.</strong
          ><br /><br /><strong>Step 1: Know the headings.</strong><br />•
          <em>Size</em>: words or numbers about length, width, height, or
          depth.<br />• <em>Weight</em>: words or numbers about heaviness—tons,
          pounds, kilograms.<br />• <em>Material</em>: the substance it is made
          from, like steel or concrete.<br />• <em>Purpose</em>: the job it does
          or why it is used.<br /><br /><strong
            >Step 2: Read one sentence at a time and look for clue
            words.</strong
          ><br /><br /><strong>Sentence:</strong> "The main support beam
          measures three feet wide, six feet deep, and 140 feet long."<br />Clue
          words: "measures," "wide," "deep," "long," and the numbers. These tell
          its dimensions, so it belongs with <em>Size</em>.<br /><br /><strong
            >Sentence:</strong
          >
          "When completed, each beam weighs just over fifty-four tons."<br />Clue
          words: "weighs" and "tons." These tell how heavy it is, so the right
          heading is <em>Weight</em>.<br /><br /><strong>Sentence:</strong>
          "Engineers chose steel that forms a thin, protective rust coat."<br />Clue
          word: "steel." This tells what the beam is made of, so match it with
          <em>Material</em>.<br /><br /><strong>Sentence:</strong> "The beam
          supports the outer traffic lane and carries loads to the piers."<br />Clue
          words: "supports" and "carries." These describe its job, so choose
          <em>Purpose</em>.<br /><br />When you use the clue words, the correct
          matches are:<br />• measurements → <em>Size</em><br />• 54 tons →
          <em>Weight</em><br />• steel → <em>Material</em><br />• supports
          traffic → <em>Purpose</em><br />Keep these clues in mind whenever you
          need to sort facts under headings!
        </p>
      </qti-content-body>
    </qti-feedback-block>
  </qti-item-body>
  <qti-response-processing
    template="https://purl.imsglobal.org/spec/qti/v3p0/rptemplates/match_correct.xml" /></qti-assessment-item>
`;
